import React, { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { AdvancedVerification } from '../utils/verification';

// Function to detect current ENS name from URL (same as above)
const detectCurrentEnsName = () => {
  const currentUrl = window.location.href;
  
  const pinmeMatch = currentUrl.match(/https:\/\/([a-z0-9]+)\.pinit\.eth\.limo/);
  if (pinmeMatch) {
    return `${pinmeMatch[1]}.pinit.eth`;
  }
  
  const ensMatch = currentUrl.match(/https:\/\/([a-z0-9-]+\.eth)\.limo/);
  if (ensMatch) {
    return ensMatch[1];
  }
  
  if (currentUrl.includes('localhost')) {
    return '3iyt6dhy.pinit.eth'; // Default for local testing
  }
  
  return null;
};

export default function VerifyBadge() {
  const { address, isConnected } = useAccount();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [currentEnsName, setCurrentEnsName] = useState(null);

  useEffect(() => {
    const verifyIntegrity = async () => {
      try {
        setLoading(true);
        
        // Detect current ENS name from URL
        const detectedEnsName = detectCurrentEnsName();
        setCurrentEnsName(detectedEnsName);
        
        if (!detectedEnsName) {
          setVerification({
            error: 'Could not detect deployment ENS name',
            overallVerified: false,
            timestamp: new Date().toISOString()
          });
          setLoading(false);
          return;
        }

        const verifier = new AdvancedVerification(detectedEnsName);
        
        const [result, gatewayList] = await Promise.all([
          verifier.getFullVerification(),
          verifier.getAccessGateways()
        ]);
        
        setVerification(result);
        setGateways(gatewayList);
      } catch (error) {
        console.error('Verification error:', error);
        setVerification({
          error: error.message,
          overallVerified: false,
          timestamp: new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    verifyIntegrity();
  }, []);

  const getVerificationStatus = () => {
    if (!verification) return 'loading';
    if (verification.error) return 'error';
    if (verification.overallVerified) return 'verified';
    return 'failed';
  };

  const statusConfig = {
    loading: { color: 'gray', icon: 'spinner', text: 'Verifying...' },
    verified: { color: 'green', icon: 'shield-check', text: 'Verified' },
    failed: { color: 'red', icon: 'shield-exclamation', text: 'Verification Failed' },
    error: { color: 'orange', icon: 'triangle-exclamation', text: 'Verification Error' }
  };

  const status = getVerificationStatus();
  const config = statusConfig[status];

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
      {/* Main Verification Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            verification?.overallVerified ? 'bg-green-100 text-green-600' :
            verification?.hasEnsRecord ? 'bg-blue-100 text-blue-600' :
            'bg-gray-100 text-gray-600'
          }`}>
            <i className={`fas fa-${
              verification?.overallVerified ? 'shield-check' : 
              verification?.hasEnsRecord ? 'link' : 'shield-exclamation'
            }`}></i>
          </div>
          <div>
            <h3 className="font-bold text-dominant">ENS Verification</h3>
            <p className="text-sm text-gray-600">
              {verification?.overallVerified ? 'Verified' : 
               verification?.hasEnsRecord ? 'ENS Record Found' : 'Checking...'}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-secondary hover:text-dominant font-medium text-sm"
        >
          {showAdvanced ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {/* ENS Status */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs font-medium text-gray-500">ENS Name</label>
          <p className="text-sm bg-gray-50 p-2 rounded font-mono">
            {currentEnsName || 'Not detected'}
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Content Hash</label>
          <p className={`text-sm font-medium px-2 py-2 rounded ${
            verification?.hasEnsRecord ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {verification?.hasEnsRecord ? 'SET' : 'NOT SET'}
          </p>
        </div>
      </div>

      {/* Quick Access - Always show these */}
      <div className="flex flex-wrap gap-2 mb-4">
        {gateways.slice(0, 2).map((gateway, index) => (
          <a
            key={index}
            href={gateway.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-secondary text-white px-3 py-2 rounded-lg hover:bg-dominant transition-colors text-sm"
          >
            <i className={`fas fa-${gateway.type === 'ens' ? 'globe' : 'cube'}`}></i>
            {gateway.name}
          </a>
        ))}
      </div>

      {/* Advanced Verification Details */}
      {showAdvanced && verification && (
        <div className="border-t pt-4 space-y-4">
          {/* Content Hash Details */}
          <div>
            <h4 className="font-medium text-dominant mb-2">Content Hash</h4>
            <div className="space-y-2">
              {verification.contentHash ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      verification.contentMatches 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {verification.contentMatches ? 'MATCHING' : 'MISMATCH'}
                    </span>
                  </div>
                  <div className="text-xs">
                    <label className="font-medium text-gray-600">ENS Record:</label>
                    <code className="font-mono bg-gray-50 p-2 rounded block mt-1 break-all">
                      {verification.contentHash}
                    </code>
                  </div>
                </>
              ) : (
                <p className="text-sm text-yellow-600">No content hash set on ENS</p>
              )}
            </div>
          </div>

          {/* All Gateways */}
          <div>
            <h4 className="font-medium text-dominant mb-2">Access Points</h4>
            <div className="grid grid-cols-1 gap-2">
              {gateways.map((gateway, index) => (
                <a
                  key={index}
                  href={gateway.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-secondary hover:text-dominant text-sm p-2 rounded hover:bg-gray-50 transition-colors"
                >
                  <i className={`fas fa-${gateway.type === 'ens' ? 'globe' : 'cube'} w-4`}></i>
                  <span className="flex-1">{gateway.name}</span>
                  <i className="fas fa-external-link-alt text-xs"></i>
                </a>
              ))}
            </div>
          </div>

          {/* Current Deployment Info */}
          {verification.currentDeployment && (
            <div>
              <h4 className="font-medium text-dominant mb-2">Current Session</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Running on PinMe:</span>
                  <span className={verification.currentDeployment.isRunningOnPinMe ? 'text-green-600' : 'text-gray-600'}>
                    {verification.currentDeployment.isRunningOnPinMe ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Running on IPFS:</span>
                  <span className={verification.currentDeployment.isRunningOnIPFS ? 'text-green-600' : 'text-gray-600'}>
                    {verification.currentDeployment.isRunningOnIPFS ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}